import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { deleteContactsCore, listContactsCore, updateContactCore, addContactToGroupCore, listContactGroupIdsCore } from '../lib/contactsCore';
import { Contact } from '../types/Contact';
import { Trash2, Check, X, Loader, AlertCircle, Users, Search, RefreshCw } from 'lucide-react';
import ContactViewModal from './ContactViewModal';
import SquareLoader from './SquareLoader';

interface DuplicateGroup {
    name: string;
    contacts: Contact[];
}

interface DuplicateDetectionProps {
    user: any;
    personalProfile?: string;
}

export default function DuplicateDetection({ user, personalProfile }: DuplicateDetectionProps) {
    const [duplicateGroups, setDuplicateGroups] = useState<DuplicateGroup[]>([]);
    const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
    const [contactsToDelete, setContactsToDelete] = useState<Set<string>>(new Set());
    const [isScanning, setIsScanning] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [scanComplete, setScanComplete] = useState(false);
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [stats, setStats] = useState({
        totalContacts: 0,
        duplicateGroups: 0,
        duplicateContacts: 0
    });
    const [isMerging, setIsMerging] = useState(false);

    const scanForDuplicates = async () => {
        setIsScanning(true);
        setScanComplete(false);
        setContactsToDelete(new Set());
        setCurrentGroupIndex(0);

        try {
            // Fetch contacts via core (respect RLS); use a high cap and sort locally by created_at
            const contacts = await listContactsCore(supabase as any, { includeArchived: false, limit: 10000, offset: 0 });
            console.log(`🔍 Loaded ${contacts?.length || 0} contacts for duplicate detection`);

            // Group contacts by normalized name
            const groupMap = new Map<string, Contact[]>();

            contacts?.forEach(contact => {
                // Create a normalized key from first and last name
                const firstName = (contact.first_name || '').trim().toLowerCase();
                const lastName = (contact.last_name || '').trim().toLowerCase();
                const key = `${firstName}_${lastName}`;

                // Skip if no name
                if (!firstName && !lastName) return;

                if (!groupMap.has(key)) {
                    groupMap.set(key, []);
                }
                groupMap.get(key)!.push(contact);
            });

            // Filter to only groups with duplicates
            const duplicates: DuplicateGroup[] = [];
            let totalDuplicates = 0;

            groupMap.forEach((contacts, key) => {
                if (contacts.length > 1) {
                    const [firstName, lastName] = key.split('_');
                    duplicates.push({
                        name: `${firstName.charAt(0).toUpperCase() + firstName.slice(1)} ${lastName.charAt(0).toUpperCase() + lastName.slice(1)}`,
                        contacts: contacts.sort((a, b) => {
                            // Sort by creation date, keeping oldest first
                            return new Date(a.created_at!).getTime() - new Date(b.created_at!).getTime();
                        })
                    });
                    totalDuplicates += contacts.length;
                }
            });

            // Sort groups by number of duplicates (most duplicates first)
            duplicates.sort((a, b) => b.contacts.length - a.contacts.length);

            setDuplicateGroups(duplicates);
            setStats({
                totalContacts: contacts?.length || 0,
                duplicateGroups: duplicates.length,
                duplicateContacts: totalDuplicates
            });
            setScanComplete(true);
        } catch (error) {
            console.error('Error scanning for duplicates:', error);
            alert('Failed to scan for duplicates');
        } finally {
            setIsScanning(false);
        }
    };

    const toggleContactForDeletion = (contactId: string) => {
        const newSet = new Set(contactsToDelete);
        if (newSet.has(contactId)) {
            newSet.delete(contactId);
        } else {
            newSet.add(contactId);
        }
        setContactsToDelete(newSet);
    };

    const markAllButFirst = () => {
        const currentGroup = duplicateGroups[currentGroupIndex];
        if (!currentGroup) return;

        const newSet = new Set(contactsToDelete);
        // Keep the first (oldest) contact, mark all others for deletion
        currentGroup.contacts.slice(1).forEach(contact => {
            newSet.add(contact.id!);
        });
        setContactsToDelete(newSet);
    };

    const clearCurrentGroup = () => {
        const currentGroup = duplicateGroups[currentGroupIndex];
        if (!currentGroup) return;

        const newSet = new Set(contactsToDelete);
        currentGroup.contacts.forEach(contact => {
            newSet.delete(contact.id!);
        });
        setContactsToDelete(newSet);
    };

    const nextGroup = () => {
        if (currentGroupIndex < duplicateGroups.length - 1) {
            setCurrentGroupIndex(currentGroupIndex + 1);
        }
    };

    const previousGroup = () => {
        if (currentGroupIndex > 0) {
            setCurrentGroupIndex(currentGroupIndex - 1);
        }
    };

    const deleteMarkedContacts = async () => {
        if (contactsToDelete.size === 0) {
            alert('No contacts marked for deletion');
            return;
        }

        // Show custom confirmation dialog instead of window.confirm
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        setShowDeleteConfirm(false);
        setIsDeleting(true);

        try {
            // Delete contacts via core
            const idsToDelete = Array.from(contactsToDelete);
            await deleteContactsCore(supabase as any, idsToDelete as string[]);

            alert(`Successfully deleted ${contactsToDelete.size} duplicate contacts`);

            // Reset state and rescan to get updated counts
            setDuplicateGroups([]);
            setContactsToDelete(new Set());
            setScanComplete(false);
            setCurrentGroupIndex(0);

            // Automatically rescan after deletion
            scanForDuplicates();
        } catch (error) {
            console.error('Error deleting contacts:', error);
            alert('Failed to delete some contacts');
        } finally {
            setIsDeleting(false);
        }
    };

    const currentGroup = duplicateGroups[currentGroupIndex];

    const mergeIntoOldest = async () => {
        const group = currentGroup;
        if (!group || group.contacts.length < 2) return;
        setIsMerging(true);
        try {
            const base = group.contacts[0]; // oldest per sort
            const others = group.contacts.slice(1);

            // Build updates: prefer existing base values; fill blanks from others; append notes
            const fieldsToFill = [
                'email', 'email_1_value', 'phone', 'mobile', 'company', 'title',
                'linkedin_url', 'twitter_url', 'website_url', 'instagram_url',
                'address', 'city', 'state', 'country', 'zip_code',
                'notes', 'private_notes', 'personal_details', 'profile_image_url'
            ] as const;

            const isEmpty = (v: any) => v == null || String(v).trim() === '';
            const updates: any = {};

            for (const other of others) {
                for (const key of fieldsToFill) {
                    const baseVal: any = (base as any)[key];
                    const otherVal: any = (other as any)[key];
                    if (key === 'notes' || key === 'private_notes') {
                        if (!isEmpty(otherVal)) {
                            if (isEmpty(baseVal)) updates[key] = otherVal;
                            else updates[key] = `${(updates[key] ?? baseVal)}\n${otherVal}`;
                        }
                    } else if (key === 'profile_image_url') {
                        if (isEmpty(baseVal) && !isEmpty(otherVal)) updates[key] = otherVal;
                    } else {
                        if (isEmpty(baseVal) && !isEmpty(otherVal) && isEmpty(updates[key])) updates[key] = otherVal;
                    }
                }
            }

            if (Object.keys(updates).length) {
                await updateContactCore(supabase as any, base.id!, updates as any);
            }

            // Union group memberships into base using core helpers
            try {
                const otherIds = others.map(o => o.id!).filter(Boolean);
                const groupIds = new Set<string>();
                for (const oid of otherIds) {
                    try {
                        const gids = await listContactGroupIdsCore(supabase as any, oid);
                        gids.forEach(g => groupIds.add(g));
                    } catch { /* ignore membership fetch errors per contact */ }
                }
                for (const gid of Array.from(groupIds)) {
                    try { await addContactToGroupCore(supabase as any, base.id!, gid); } catch { /* ignore duplicate membership errors */ }
                }
            } catch { /* non-fatal */ }

            // Delete others after merge
            const idsToDelete = others.map(o => o.id!).filter(Boolean);
            if (idsToDelete.length) await deleteContactsCore(supabase as any, idsToDelete as string[]);

            alert('Merged duplicates into oldest contact');
            // Refresh scan
            await scanForDuplicates();
        } catch (e) {
            console.error('Merge failed', e);
            alert('Failed to merge duplicates');
        } finally {
            setIsMerging(false);
        }
    };

    return (
        <div className="space-y-6 pt-6">
            {/* Scan Button and Stats */}
            {!scanComplete ? (
                <div className="text-center py-8">
                    <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-white mb-2">Duplicate Contact Detection</h3>
                    <p className="text-gray-400 mb-6">Scan your contacts to find and remove duplicates</p>
                    <button
                        onClick={scanForDuplicates}
                        disabled={isScanning}
                        className="inline-flex items-center px-6 py-3 bg-white text-black hover:bg-gray-100 transition-colors font-semibold text-sm tracking-wide disabled:opacity-50"
                    >
                        {isScanning ? (
                            <>
                                <SquareLoader size="xs" className="mr-2" />
                                SCANNING CONTACTS...
                            </>
                        ) : (
                            <>
                                <Search className="w-5 h-5 mr-2" />
                                SCAN FOR DUPLICATES
                            </>
                        )}
                    </button>
                </div>
            ) : (
                <>
                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4">
                        <div className="bg-gray-900 border border-gray-800 p-4 rounded-lg text-center">
                            <div className="text-2xl font-bold text-white">{stats.totalContacts}</div>
                            <div className="text-sm text-gray-400">Total Contacts</div>
                        </div>
                        <div className="bg-gray-900 border border-gray-800 p-4 rounded-lg text-center">
                            <div className="text-2xl font-bold text-yellow-400">{stats.duplicateGroups}</div>
                            <div className="text-sm text-gray-400">Duplicate Groups</div>
                        </div>
                        <div className="bg-gray-900 border border-gray-800 p-4 rounded-lg text-center">
                            <div className="text-2xl font-bold text-red-400">{contactsToDelete.size}</div>
                            <div className="text-sm text-gray-400">Marked for Deletion</div>
                        </div>
                    </div>

                    {/* Rescan Button */}
                    <div className="flex justify-center">
                        <button
                            onClick={scanForDuplicates}
                            disabled={isScanning}
                            className="inline-flex items-center px-4 py-2 bg-gray-800 text-white hover:bg-gray-700 transition-colors font-medium text-sm disabled:opacity-50"
                        >
                            {isScanning ? (
                                <>
                                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                                    SCANNING...
                                </>
                            ) : (
                                <>
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    RESCAN FOR DUPLICATES
                                </>
                            )}
                        </button>
                    </div>

                    {duplicateGroups.length === 0 ? (
                        <div className="text-center py-8 bg-gray-900 border border-gray-800 rounded-lg">
                            <Check className="w-12 h-12 text-green-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-white mb-2">No Duplicates Found!</h3>
                            <p className="text-gray-400">Your contact list is clean</p>
                        </div>
                    ) : currentGroup && (
                        <>
                            {/* Navigation */}
                            <div className="flex items-center justify-between bg-gray-900 border border-gray-800 p-4 rounded-lg">
                                <button
                                    onClick={previousGroup}
                                    disabled={currentGroupIndex === 0}
                                    className="px-4 py-2 bg-gray-800 text-white hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Previous
                                </button>
                                <div className="text-center">
                                    <div className="text-white font-medium">Group {currentGroupIndex + 1} of {duplicateGroups.length}</div>
                                    <div className="text-sm text-gray-400">{currentGroup.name} ({currentGroup.contacts.length} contacts)</div>
                                </div>
                                <button
                                    onClick={nextGroup}
                                    disabled={currentGroupIndex === duplicateGroups.length - 1}
                                    className="px-4 py-2 bg-gray-800 text-white hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Next
                                </button>
                            </div>

                            {/* Quick Actions */}
                            <div className="flex gap-3">
                                <button
                                    onClick={clearCurrentGroup}
                                    className="flex-1 px-4 py-2 bg-gray-700 text-white hover:bg-gray-600 transition-colors text-sm font-medium"
                                >
                                    Clear Selections
                                </button>
                                <button
                                    onClick={mergeIntoOldest}
                                    disabled={isMerging}
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white hover:bg-blue-500 transition-colors text-sm font-medium disabled:opacity-50"
                                    title="Smart merge: fill blanks in the oldest contact, combine notes, and delete other duplicates"
                                >
                                    {isMerging ? 'Merging…' : 'Merge All into Oldest'}
                                </button>
                            </div>

                            {/* Contact Cards */}
                            <div className="space-y-3">
                                {currentGroup.contacts.map((contact, index) => (
                                    <div
                                        key={contact.id}
                                        onClick={() => setSelectedContact(contact)}
                                        className={`bg-gray-900 border p-4 rounded-lg transition-all duration-200 cursor-pointer ${contactsToDelete.has(contact.id!) ? 'border-red-500 bg-red-500/10' : 'border-gray-800 hover:border-gray-700'
                                            }`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start gap-4 flex-1">
                                                {/* Contact Image */}
                                                <div className="flex-shrink-0">
                                                    {contact.profile_image_url ? (
                                                        <img
                                                            src={contact.profile_image_url}
                                                            alt={`${contact.first_name} ${contact.last_name}`}
                                                            className="w-16 h-16 rounded-lg object-cover bg-gray-800"
                                                            onError={(e) => {
                                                                e.currentTarget.style.display = 'none';
                                                                e.currentTarget.nextElementSibling!.style.display = 'flex';
                                                            }}
                                                        />
                                                    ) : null}
                                                    <div
                                                        className={`w-16 h-16 rounded-lg bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center text-gray-300 text-xl font-semibold ${contact.profile_image_url ? 'hidden' : 'flex'}`}
                                                    >
                                                        {contact.first_name?.[0]?.toUpperCase() || contact.last_name?.[0]?.toUpperCase() || '?'}
                                                    </div>
                                                </div>

                                                {/* Contact Details */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                                                        <h4 className="text-lg font-medium text-white truncate">
                                                            {contact.first_name} {contact.last_name}
                                                        </h4>
                                                        {index === 0 && (
                                                            <span className="text-xs bg-green-600 text-white px-2 py-1 rounded whitespace-nowrap">OLDEST</span>
                                                        )}
                                                        {contactsToDelete.has(contact.id!) && (
                                                            <span className="text-xs bg-red-600 text-white px-2 py-1 rounded whitespace-nowrap">TO DELETE</span>
                                                        )}
                                                    </div>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                                        <div className="truncate">
                                                            <span className="text-gray-500">Email:</span>{' '}
                                                            <span className="text-gray-300">{contact.email || 'N/A'}</span>
                                                        </div>
                                                        <div className="truncate">
                                                            <span className="text-gray-500">Phone:</span>{' '}
                                                            <span className="text-gray-300">{contact.phone || 'N/A'}</span>
                                                        </div>
                                                        <div className="truncate">
                                                            <span className="text-gray-500">Company:</span>{' '}
                                                            <span className="text-gray-300">{contact.company || 'N/A'}</span>
                                                        </div>
                                                        <div className="truncate">
                                                            <span className="text-gray-500">Created:</span>{' '}
                                                            <span className="text-gray-300">
                                                                {new Date(contact.created_at!).toLocaleDateString()}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Action Button */}
                                            <div className="ml-4">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Prevent card click when clicking delete button
                                                        toggleContactForDeletion(contact.id!);
                                                    }}
                                                    className={`p-2 rounded-lg transition-colors ${contactsToDelete.has(contact.id!)
                                                        ? 'bg-red-600 text-white hover:bg-red-700'
                                                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                                                        }`}
                                                    title={contactsToDelete.has(contact.id!) ? 'Unmark for deletion' : 'Mark for deletion'}
                                                >
                                                    {contactsToDelete.has(contact.id!) ? (
                                                        <X className="w-5 h-5" />
                                                    ) : (
                                                        <Trash2 className="w-5 h-5" />
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {/* Action Buttons */}
                    {duplicateGroups.length > 0 && (
                        <div className="flex gap-3 pt-4 border-t border-gray-800">
                            <button
                                onClick={() => {
                                    setScanComplete(false);
                                    setDuplicateGroups([]);
                                    setContactsToDelete(new Set());
                                }}
                                className="flex-1 px-6 py-3 bg-gray-700 text-white hover:bg-gray-600 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={deleteMarkedContacts}
                                disabled={contactsToDelete.size === 0 || isDeleting}
                                className="flex-1 px-6 py-3 bg-red-600 text-white hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isDeleting ? (
                                    <>
                                        <Loader className="w-5 h-5 mr-2 animate-spin inline" />
                                        DELETING...
                                    </>
                                ) : (
                                    `DELETE ${contactsToDelete.size} CONTACTS`
                                )}
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Contact View Modal */}
            {selectedContact && (
                <ContactViewModal
                    contact={selectedContact}
                    onClose={() => setSelectedContact(null)}
                    onSave={() => {
                        // Optionally refresh the duplicate scan after editing
                        // scanForDuplicates();
                    }}
                    user={user}
                    personalProfile={personalProfile}
                />
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <AlertCircle className="w-6 h-6 text-red-400" />
                            <h3 className="text-lg font-medium text-white">Confirm Deletion</h3>
                        </div>
                        <p className="text-gray-300 mb-6">
                            Are you sure you want to delete {contactsToDelete.size} contact(s)? This action cannot be undone.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="flex-1 px-4 py-2 bg-gray-700 text-white hover:bg-gray-600 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="flex-1 px-4 py-2 bg-red-600 text-white hover:bg-red-700 transition-colors font-medium"
                            >
                                Delete {contactsToDelete.size} Contacts
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
